import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { db } from "../../utils/firebase";
import { MdRefresh } from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import BillModal from "../UserDashboard/BillModel";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import * as XLSX from "xlsx";

const WeeklyBookings = () => {
  const [groupedBookings, setGroupedBookings] = useState({});
  const [loading, setLoading] = useState(false);
  const restaurantEmail = localStorage.getItem("user");
  const businessName =
    localStorage.getItem("businessName") || "Our Business";

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ✅ NEW STATES FOR REMINDER
  const [isReminderOpen, setIsReminderOpen] = useState(false);
  const [reminderMessage, setReminderMessage] = useState("");

  // ✅ STAFF STATES
  const [staffList, setStaffList] = useState([]);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  // ✅ STAFF REPORT STATES
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [staffReport, setStaffReport] = useState({});
  const [reportLoading, setReportLoading] = useState(false);

  const [selectedReportDate, setSelectedReportDate] = useState(
    new Date().toLocaleDateString("en-CA")
  );

  // cache per date
  const [reportCache, setReportCache] = useState({});

  const getTodayISO = () => {
    return new Date().toISOString().split("T")[0];
  };

  const getNext7DaysISO = () => {
    const date = new Date();
    date.setDate(date.getDate() + 6);
    return date.toISOString().split("T")[0];
  };

  const fetchWeeklyBookings = async () => {
    setLoading(true);
    try {
      const todayISO = getTodayISO();
      const next7DaysISO = getNext7DaysISO();

      const bookingsRef = collection(db, "orders");

      const q = query(
        bookingsRef,
        where("email", "==", restaurantEmail),
        where("orderType", "==", "BOOKING"),
        where("preferredDate", ">=", todayISO),
        where("preferredDate", "<=", next7DaysISO),
        where("isBilled", "==", false),
        orderBy("preferredDate", "asc"),
        orderBy("preferredTime", "asc"),
        limit(100)
      );

      const snapshot = await getDocs(q);

      const rawBookings = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const grouped = {};

      rawBookings.forEach((booking) => {
        const phone = booking.customerPhone;
        const customerName = booking.customerName;
        const preferredDate = booking.preferredDate;
        const preferredTime = booking.preferredTime;

        const parts = booking.order?.split("|") || [];
        const itemsRaw = parts[0]?.replace("Items:", "").trim();
        const total = parts
          .find((p) => p.includes("Total:"))
          ?.match(/₹(\d+)/)?.[1];

        const groupKey = `${preferredDate}|${preferredTime}|${phone}|${customerName}`;

        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            orderType: "BOOKING",
            phone,
            customerName,
            preferredDate,
            preferredTime,
            orders: [],
            totalBill: 0,
            isFullyBilled: true,
          };
        }

        grouped[groupKey].orders.push({
          ...booking,
          items: itemsRaw,
          createdDateTime: parseISO(booking.createdDateTime),
        });

        if (!booking.isBilled) {
          grouped[groupKey].isFullyBilled = false;
        }

        grouped[groupKey].totalBill += total ? parseInt(total) : 0;
      });

      setGroupedBookings(grouped);
    } catch (error) {
      console.error("Error fetching weekly bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignStaff = async () => {
    if (!selectedStaff || !selectedOrder) return;

    try {
      await updateDoc(doc(db, "orders", selectedOrder.id), {
        attendedBy: selectedStaff,
      });

      setIsAssignOpen(false);
      setSelectedStaff("");
      setSelectedOrder(null);

      fetchWeeklyBookings(); // refresh

    } catch (error) {
      console.error(error);
      alert("Failed to assign staff");
    }
  };

  const openStaffReport = async () => {
    setIsReportOpen(true);

    const selectedDate = selectedReportDate;

    // ✅ check cache first
    if (reportCache[selectedDate]) {
      setStaffReport(reportCache[selectedDate]);
      return;
    }

    setReportLoading(true);

    try {
      const q = query(
        collection(db, "orders"),
        where("email", "==", restaurantEmail),
        where("orderType", "==", "BOOKING"),
        where("preferredDate", "==", selectedDate)//,
        //where("isBilled", "==", true)
      );

      const snapshot = await getDocs(q);

      const orders = snapshot.docs.map((doc) => doc.data());

      const report = {};

      orders.forEach((order) => {
        if (!order.attendedBy) return;

        const staff = order.attendedBy;

        const total = order.order
          ?.split("|")
          .find((p) => p.includes("Total:"))
          ?.match(/₹(\d+)/)?.[1];

        const items = order.order?.split("|")[0]?.replace("Items:", "").trim();

        if (!report[staff]) {
          report[staff] = {
            customers: 0,
            revenue: 0,
            details: [],
          };
        }

        report[staff].customers += 1;
        report[staff].revenue += total ? parseInt(total) : 0;

        report[staff].details.push({
          customerName: order.customerName,
          service: items,
        });
      });

      setStaffReport(report);

      // ✅ store in cache
      setReportCache((prev) => ({
        ...prev,
        [selectedDate]: report,
      }));

    } catch (error) {
      console.error(error);
    } finally {
      setReportLoading(false);
    }
  };

  const openStaffReportForDate = async (selectedDate) => {
    setIsReportOpen(true);

    // ✅ cache check
    if (reportCache[selectedDate]) {
      setStaffReport(reportCache[selectedDate]);
      return;
    }

    setReportLoading(true);

    try {
      const q = query(
        collection(db, "orders"),
        where("email", "==", restaurantEmail),
        where("orderType", "==", "BOOKING"),
        where("preferredDate", "==", selectedDate)
      );

      const snapshot = await getDocs(q);
      const orders = snapshot.docs.map((doc) => doc.data());

      const report = {};

      orders.forEach((order) => {
        if (!order.attendedBy) return;

        const staff = order.attendedBy;

        const total = order.order
          ?.split("|")
          .find((p) => p.includes("Total:"))
          ?.match(/₹(\d+)/)?.[1];

        const items = order.order?.split("|")[0]?.replace("Items:", "").trim();

        if (!report[staff]) {
          report[staff] = {
            customers: 0,
            revenue: 0,
            details: [],
          };
        }

        report[staff].customers += 1;
        report[staff].revenue += total ? parseInt(total) : 0;

        report[staff].details.push({
          customerName: order.customerName,
          service: items,
        });
      });

      setStaffReport(report);

      // ✅ cache
      setReportCache((prev) => ({
        ...prev,
        [selectedDate]: report,
      }));

    } catch (error) {
      console.error(error);
    } finally {
      setReportLoading(false);
    }
  };

  const downloadExcel = () => {
    const data = [];

    Object.entries(staffReport).forEach(([staff, info]) => {
      info.details.forEach((d) => {
        data.push({
          Staff: staff,
          Customer: d.customerName,
          Service: d.service,
        });
      });

      data.push({
        Staff: staff,
        Customer: "TOTAL",
        Service: `${info.customers} customers • ₹${info.revenue}`,
      });
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Staff Report");

    // ✅ USE SELECTED DATE
    XLSX.writeFile(wb, `staff-report-${selectedReportDate}.xlsx`);
  };

  useEffect(() => {
    if (restaurantEmail) {
      fetchWeeklyBookings();
    }
  }, [restaurantEmail]);


  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const snap = await getDoc(doc(db, "userDetails", restaurantEmail));
        if (snap.exists()) {
          const data = snap.data();
          setStaffList(data?.businessRules?.staff || []);
        }
      } catch (err) {
        console.error("Error fetching staff", err);
      }
    };

    if (restaurantEmail) fetchStaff();
  }, [restaurantEmail]);

  // ✅ GENERATE REMINDER MESSAGE
  const generateReminderMessage = (group) => {
    return `Hello ${group.customerName},

This is a friendly reminder that your appointment is scheduled on ${group.preferredDate} at ${group.preferredTime} with ${businessName}.

We look forward to serving you.
If you need to reschedule, please inform us in advance.

Thank you!`;
  };

  const renderGroup = (groupKey, group) => (
    <div
      key={groupKey}
      className="border border-gray-200 rounded-xl p-3 bg-white shadow-sm hover:shadow-md transition text-xs"
    >
      {/* Top Row */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 font-medium">
            {group.preferredDate}
          </span>
          <span className="px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 font-medium">
            {group.preferredTime}
          </span>
        </div>

        <div className="text-right leading-tight">
          <div className="font-medium text-gray-800">
            {group.customerName}
          </div>
          <div className="text-gray-500 text-[11px]">
            {group.phone}
          </div>
        </div>
      </div>

      {/* Orders */}
      {group.orders.map((order, index) => (
        <div
          key={order.id}
          className="py-1 border-t border-gray-100 first:border-0"
        >
          <div className="text-gray-800 font-medium">
            #{index + 1} •{" "}
            {formatDistanceToNowStrict(order.createdDateTime, {
              addSuffix: true,
            })}
          </div>

          <div className="text-gray-500 truncate">
            {order.items}
          </div>

          {/* ✅ STAFF SECTION */}
          <div className="flex items-center justify-between mt-1">

            {order.attendedBy ? (
              <div className="text-[11px] text-gray-600">
                👤 <span className="font-medium">{order.attendedBy}</span>
              </div>
            ) : (
              <div className="text-[11px] text-gray-400 italic">
                Not assigned
              </div>
            )}

            <button
              onClick={() => {
                setSelectedOrder(order);
                setSelectedStaff(order.attendedBy || "");
                setIsAssignOpen(true);
              }}
              className="text-[10px] px-2 py-0.5 bg-[#6cbb23] text-white rounded-md hover:opacity-90"
            >
              {order.attendedBy ? "Change" : "Assign"}
            </button>

          </div>
        </div>
      ))}

      {/* Bottom Row */}
      <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
        <span className="font-semibold text-green-600">
          ₹{group.totalBill}
        </span>

        <div className="flex gap-2">
          {/* ✅ Reminder Button */}
          <button
            onClick={() => {
              const message = generateReminderMessage(group);
              setReminderMessage(message);
              setSelectedGroup(group);
              setIsReminderOpen(true);
            }}
            className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition"
          >
            <FaWhatsapp size={12} />
            Reminder
          </button>

          {/* Existing Bill Button (UNCHANGED) */}
          <button
            onClick={() => {
              setSelectedGroup(group);
              setIsModalOpen(true);
            }}
            className="px-3 py-1 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
          >
            Bill
          </button>
        </div>
      </div>
    </div>
  );

  const groupedArray = Object.entries(groupedBookings);

  return (
    <div className="w-full bg-white border border-gray-200 rounded-2xl shadow-md p-4 mt-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-sm font-semibold text-gray-900">
          📅 Upcoming 7 Days
        </h2>

        <div className="flex items-center gap-2">
          <button
            onClick={() => openStaffReportForDate(selectedReportDate)}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition"
          >
            📊
          </button>
          <button
            onClick={() => {
              setReportCache({}); // ✅ clear all cached reports
              fetchWeeklyBookings();
            }}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition"
          >
            <MdRefresh size={16} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-6 text-gray-400 text-xs animate-pulse">
          Loading bookings...
        </div>
      ) : groupedArray.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-xs">
          No bookings in next 7 days.
        </div>
      ) : (
        <div className="max-h-[600px] overflow-y-auto space-y-2">
          {groupedArray.map(([key, group]) =>
            renderGroup(key, group)
          )}
        </div>
      )}

      {/* Existing Bill Modal */}
      <BillModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        group={selectedGroup}
        onBillingSuccess={fetchWeeklyBookings}
      />

      {/* ✅ Reminder Preview Modal */}
      {isReminderOpen && selectedGroup && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl p-4">
            <h3 className="text-sm font-semibold mb-2 text-gray-800">
              📩 WhatsApp Reminder Preview
            </h3>

            <div className="bg-gray-50 border rounded-md p-3 text-xs whitespace-pre-line text-gray-700 max-h-60 overflow-y-auto">
              {reminderMessage}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setIsReminderOpen(false)}
                className="px-3 py-1 text-xs rounded-md bg-gray-200 hover:bg-gray-300"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  let rawPhone = selectedGroup.phone || "";

                  // Remove spaces, +, -, etc.
                  let cleanedPhone = rawPhone.replace(/\D/g, "");

                  // If already starts with 91 and length is 12 → keep it
                  if (cleanedPhone.startsWith("91") && cleanedPhone.length === 12) {
                    // do nothing
                  }
                  // If 10 digit Indian number → add 91
                  else if (cleanedPhone.length === 10) {
                    cleanedPhone = "91" + cleanedPhone;
                  }

                  // Else → leave as is (fallback safety)

                  const encodedMessage = encodeURIComponent(reminderMessage);
                  const whatsappUrl = `https://wa.me/${cleanedPhone}?text=${encodedMessage}`;
                  window.open(whatsappUrl, "_blank");
                  setIsReminderOpen(false);
                }}
                className="flex items-center gap-1 px-3 py-1 text-xs rounded-md bg-green-600 text-white hover:bg-green-700"
              >
                <FaWhatsapp size={12} />
                Send via WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ ASSIGN STAFF MODAL */}
      {isAssignOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl p-4">

            {staffList.length === 0 ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  You don’t have any staff listed.
                  <br />
                  Please visit menu editor page to add staff.
                </p>

                <button
                  onClick={() => setIsAssignOpen(false)}
                  className="w-full bg-gray-200 py-2 rounded"
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <h3 className="text-sm font-semibold mb-3 text-gray-800">
                  👥 Assign Staff
                </h3>

                <select
                  value={selectedStaff}
                  onChange={(e) => setSelectedStaff(e.target.value)}
                  className="w-full border rounded-lg p-2 text-sm mb-4 focus:ring-2 focus:ring-[#6cbb23]"
                >
                  <option value="">Select Staff</option>
                  {staffList
                    .filter((s) => s.active)
                    .map((staff, i) => (
                      <option key={i} value={staff.name}>
                        {staff.name} ({staff.id})
                      </option>
                    ))}
                </select>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setIsAssignOpen(false)}
                    className="px-3 py-1 text-xs rounded-md bg-gray-200 hover:bg-gray-300"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleAssignStaff}
                    className="px-3 py-1 text-xs rounded-md bg-[#6cbb23] text-white hover:opacity-90"
                  >
                    Update
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ✅ STAFF REPORT MODAL */}
      {/* ✅ STAFF REPORT MODAL (PREMIUM) */}
      {isReportOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">

          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-gray-200 flex flex-col max-h-[85vh] overflow-hidden">

            {/* 🔥 HEADER (Sticky) */}
            <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-[#f4f9ed] to-white sticky top-0 z-10">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                📊 Staff Performance
              </h3>

              <input
                type="date"
                value={selectedReportDate}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setSelectedReportDate(newDate);
                  openStaffReportForDate(newDate);
                }}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-[#6cbb23]"
              />
            </div>

            {/* 🔥 CONTENT (Scrollable) */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">

              {reportLoading ? (
                <div className="text-center text-gray-400 py-10 text-sm">
                  Loading report...
                </div>
              ) : Object.keys(staffReport).length === 0 ? (
                <div className="text-center text-gray-400 py-10 text-sm">
                  No data available
                </div>
              ) : (
                Object.entries(staffReport).map(([staff, data]) => (
                  <div
                    key={staff}
                    className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 hover:shadow-md transition"
                  >

                    {/* Staff Header */}
                    <div className="flex justify-between items-center mb-2">
                      <div className="font-semibold text-gray-800 text-sm">
                        {staff}
                      </div>

                      <div className="text-[11px] text-gray-600 bg-[#f4f9ed] px-2 py-1 rounded-md">
                        {data.customers} clients • ₹{data.revenue}
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-gray-100 mb-2"></div>

                    {/* Compact List */}
                    <div className="space-y-1 text-[11px] text-gray-600 max-h-32 overflow-y-auto pr-1">

                      {data.details.map((d, i) => (
                        <div key={i} className="flex justify-between gap-2">
                          <span className="truncate">{d.customerName}</span>
                          <span className="truncate text-gray-400 max-w-[140px] text-right">
                            {d.service}
                          </span>
                        </div>
                      ))}

                    </div>
                  </div>
                ))
              )}

            </div>

            {/* 🔥 FOOTER (Sticky) */}
            <div className="flex justify-between items-center px-5 py-3 border-t bg-white sticky bottom-0">

              <button
                onClick={downloadExcel}
                className="px-4 py-2 text-xs rounded-lg bg-gradient-to-r from-[#6cbb23] to-[#4f8f1a] text-white font-medium shadow hover:shadow-md transition active:scale-[0.98]"
              >
                ⬇ Download Excel
              </button>

              <button
                onClick={() => setIsReportOpen(false)}
                className="px-4 py-2 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
              >
                Close
              </button>

            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default WeeklyBookings;